// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Pipeline Builder Component
 *
 * Dedicated canvas for building document processing pipelines.
 * Handles runtime nodes: datasource, processing, analysis, connectors, control.
 * Manages pipeline CRUD operations and edge connections.
 */

import { forwardRef, useCallback, useState, useMemo, useEffect, useImperativeHandle, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  Panel,
  useReactFlow,
  MarkerType,
} from '@xyflow/react'
import type { Node, DefaultEdgeOptions } from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import { ConfigurationPanel, type ConfigurationPanelHandle } from '@/subsystems/palette'
import { PipelineSelector } from '../PipelineSelector'
import { arrangePipelineNodes } from '../../utils/arrangePipelineNodes'
import { ConfirmDialog } from '@/components/forms'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
} from '@/components/ui'
import { AlignHorizontalJustifyCenter, EyeOff, Eye } from 'lucide-react'
import type { PipelineNodeData } from '../../../../core/contracts'
import { getPipelineNode } from '../../../../core/pipeline-registry'
import { toast } from 'sonner'
import { buildPipelineNodeTypes } from '../../utils/build-pipeline-node-types'
import { usePipelineFlowState } from '../../hooks/usePipelineFlowState'
import { usePipelineSelection } from '../../hooks/usePipelineSelection'
import { usePipelinePersistence } from '../../hooks/usePipelinePersistence'
import { LAYOUT, FIT_VIEW_OPTIONS, ZOOM } from '../../utils/layout-constants'
import type { Pipeline } from '../../types'

/** Arrange options - uses centralized layout constants */
const ARRANGE_OPTIONS = {
  stepX: LAYOUT.stepX,
  startX: LAYOUT.startX,
  defaultY: LAYOUT.defaultY,
} as const

/** Default edge styling with arrow markers */
const DEFAULT_EDGE_OPTIONS: DefaultEdgeOptions = {
  style: { strokeWidth: 2, stroke: '#94a3b8' },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 16,
    height: 16,
    color: '#94a3b8',
  },
}

export interface PipelineConfigContext {
  pipelineName?: string | null
  pipelineDescription?: string | null
  nodeLabel: string
  dirty: boolean
  onCancel: () => void
  onSave: () => void
}

export interface PipelineCanvasHandle {
  /** Stage a node add at the canvas center — the tap-to-add path for palettes
      that can't drag onto the canvas (touch screens). */
  requestAddNode: (nodeDefinition: string) => void
}

export interface PipelineCanvasProps {
  workspaceId: string
  organizationId?: string
  /** Owning agent — the builder edits the agent's own pipeline copy. */
  agentId?: string
  /** Pipelines from workspace config */
  pipelines?: Record<string, Pipeline> | null
  onDragOver?: (event: React.DragEvent) => void
  onDrop?: (event: React.DragEvent) => void
  onConfigModeChange?: (open: boolean) => void
  onConfigContextChange?: (context: PipelineConfigContext | null) => void
}

export const PipelineCanvas = forwardRef<PipelineCanvasHandle, PipelineCanvasProps>(({
  workspaceId,
  organizationId,
  agentId,
  pipelines: pipelinesProp,
  onDragOver,
  onDrop,
  onConfigModeChange,
  onConfigContextChange,
}, ref) => {
  const reactFlowInstance = useReactFlow()
  const {
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    onConnect,
  } = usePipelineFlowState()

  const {
    setSelectedNodeId,
    selectedNode,
    selectNode,
  } = usePipelineSelection(nodes)

  const [searchParams, setSearchParams] = useSearchParams()
  const showConfig = searchParams.get('pconfig') === 'true'
  const setShowConfig = useCallback((value: boolean | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) {
        next.set('pconfig', 'true')
      } else {
        next.delete('pconfig')
      }
      return next
    }, { replace: true })
  }, [setSearchParams])
  const [isConfigDirty, setIsConfigDirty] = useState(false)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [pipelineToDelete, setPipelineToDelete] = useState<string | null>(null)
  const [nodeDeleteConfirmOpen, setNodeDeleteConfirmOpen] = useState(false)
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null)
  const [pendingNode, setPendingNode] = useState<{
    flowId: string
    type: string
    position: { x: number; y: number }
    label: string
    nodeDefinition: string
    defaultInstanceId: string
  } | null>(null)
  const [pendingInstanceId, setPendingInstanceId] = useState('')
  const configPanelRef = useRef<ConfigurationPanelHandle | null>(null)
  const latestNodesRef = useRef<typeof nodes>(nodes)
  const latestEdgesRef = useRef<typeof edges>(edges)

  // Keep refs up to date with latest nodes/edges
  useEffect(() => {
    latestNodesRef.current = nodes
    latestEdgesRef.current = edges
  }, [nodes, edges])

  const nodeTypes = useMemo(() => buildPipelineNodeTypes(), [])

  const resetConfigState = useCallback(() => {
    void setShowConfig(false)
    setIsConfigDirty(false)
    onConfigModeChange?.(false)
    onConfigContextChange?.(null)
  }, [onConfigContextChange, onConfigModeChange, setShowConfig])

  const handleArrange = useCallback(() => {
    // Use ref to avoid stale closure when called after setEdges but before re-render
    setNodes(currentNodes => arrangePipelineNodes(currentNodes, latestEdgesRef.current, ARRANGE_OPTIONS))
    requestAnimationFrame(() => reactFlowInstance.fitView(FIT_VIEW_OPTIONS))
  }, [reactFlowInstance, setNodes])

  const {
    pipelines,
    activePipelineId,
    isActiveBinding,
    loadingPipelines,
    isSaving,
    isCreating,
    handleSave,
    handleEditPipeline,
    handleTogglePipelineHidden,
    handlePipelineSelect,
    handleClonePipeline,
    handleCreatePipeline,
    handleDeletePipeline,
  } = usePipelinePersistence({
    organizationId,
    workspaceId,
    agentId,
    nodes,
    edges,
    setNodes,
    setEdges,
    setSelectedNodeId,
    onAfterPipelineLoad: resetConfigState,
    onArrange: handleArrange,
    pipelines: pipelinesProp,
  })

  // Wrap onConnect to auto-save after edge creation
  const handleConnect = useCallback((connection: Parameters<typeof onConnect>[0]) => {
    onConnect(connection)
    // Save after React processes the edge addition
    requestAnimationFrame(() => {
      handleSave(latestNodesRef.current, latestEdgesRef.current)
    })
  }, [onConnect, handleSave])

  /** All connections are valid - nodes handle their own input flexibility */
  const isValidConnection = useCallback(() => true, [])

  /** Double-click opens the configuration panel for the selected node */
  const handleNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node<PipelineNodeData>) => {
    selectNode(event, node)
    void setShowConfig(true)
    setIsConfigDirty(false)
  }, [selectNode, setShowConfig])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    onDragOver?.(event)
  }, [onDragOver])

  /** Stage a node for the instance-ID dialog — shared by drag-drop and tap-to-add */
  const stageNode = useCallback((nodeDefinition: string, position: { x: number; y: number }) => {
    const node = getPipelineNode(nodeDefinition)
    if (!node) {
      toast.error('Selected node is not registered')
      return
    }

    const existingCount = nodes.filter(n => n.data?.nodeDefinition === nodeDefinition).length
    const timestamp = Date.now()
    const flowId = `${nodeDefinition}-${timestamp}`
    const defaultInstanceId = `${activePipelineId ?? 'draft'}.${nodeDefinition}#${existingCount}`

    setPendingNode({
      flowId,
      type: node.id,
      position,
      label: node.display.label,
      nodeDefinition: node.id,
      defaultInstanceId,
    })
    setPendingInstanceId(defaultInstanceId)
  }, [activePipelineId, nodes])

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()

    const nodeDefinition = event.dataTransfer.getData('application/reactflow')
    if (!nodeDefinition) {
      return
    }

    stageNode(nodeDefinition, reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }))
    onDrop?.(event)
  }, [stageNode, onDrop, reactFlowInstance])

  const canvasContainerRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    requestAddNode: (nodeDefinition: string) => {
      const rect = canvasContainerRef.current?.getBoundingClientRect()
      const position = rect
        ? reactFlowInstance.screenToFlowPosition({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          })
        : { x: 0, y: 0 }
      stageNode(nodeDefinition, position)
    },
  }), [reactFlowInstance, stageNode])

  const confirmAddNode = useCallback(() => {
    if (!pendingNode || !pendingInstanceId.trim()) return

    const newNode: Node<PipelineNodeData> = {
      id: pendingNode.flowId,
      type: pendingNode.type,
      position: pendingNode.position,
      data: {
        label: pendingNode.label,
        configured: false,
        nodeDefinition: pendingNode.nodeDefinition,
        instanceId: pendingInstanceId.trim(),
        config: {},
      },
    }

    const updatedNodes = [...latestNodesRef.current, newNode]
    setNodes(updatedNodes)
    handleSave(updatedNodes, latestEdgesRef.current)
    setPendingNode(null)
    setPendingInstanceId('')
  }, [pendingNode, pendingInstanceId, setNodes, handleSave])

  const cancelAddNode = useCallback(() => {
    setPendingNode(null)
    setPendingInstanceId('')
  }, [])

  const handleCloseConfig = useCallback((options?: { force?: boolean }) => {
    if (!showConfig) return true

    if (!options?.force && isConfigDirty) {
      setDiscardConfirmOpen(true)
      return false
    }

    void setShowConfig(false)
    setIsConfigDirty(false)
    void setSelectedNodeId(null)
    return true
  }, [isConfigDirty, setSelectedNodeId, showConfig, setShowConfig])

  const confirmDiscardChanges = useCallback(() => {
    void setShowConfig(false)
    setIsConfigDirty(false)
    void setSelectedNodeId(null)
    setDiscardConfirmOpen(false)
  }, [setSelectedNodeId, setShowConfig])

  /** Save node config locally, then persist entire pipeline to backend */
  const handleSaveConfig = useCallback(async () => {
    configPanelRef.current?.save()

    // Wait for React state updates to complete before reading refs
    await new Promise(resolve => setTimeout(resolve, 0))

    await handleSave(latestNodesRef.current, latestEdgesRef.current)
  }, [handleSave])

  const handleRequestDeletePipeline = useCallback((pipelineId: string) => {
    setPipelineToDelete(pipelineId)
    setDeleteConfirmOpen(true)
  }, [])

  const confirmDeletePipeline = useCallback(async () => {
    if (!pipelineToDelete) {
      setDeleteConfirmOpen(false)
      return
    }

    try {
      await handleDeletePipeline(pipelineToDelete)
    } finally {
      setPipelineToDelete(null)
      setDeleteConfirmOpen(false)
    }
  }, [handleDeletePipeline, pipelineToDelete])

  // Node deletion: intercept delete key / backspace, show confirmation
  const handleNodesDelete = useCallback((nodesToDelete: Node<PipelineNodeData>[]) => {
    if (nodesToDelete.length === 0 || isActiveBinding) return
    // Store the first node to delete (ReactFlow sends array but typically one at a time)
    setNodeToDelete(nodesToDelete[0].id)
    setNodeDeleteConfirmOpen(true)
  }, [isActiveBinding])

  const confirmDeleteNode = useCallback(() => {
    if (!nodeToDelete) {
      setNodeDeleteConfirmOpen(false)
      return
    }

    const updatedNodes = latestNodesRef.current.filter(n => n.id !== nodeToDelete)
    const updatedEdges = latestEdgesRef.current.filter(
      e => e.source !== nodeToDelete && e.target !== nodeToDelete
    )
    setNodes(updatedNodes)
    setEdges(updatedEdges)
    setNodeToDelete(null)
    setNodeDeleteConfirmOpen(false)

    // Close config panel if the deleted node was selected
    if (selectedNode?.id === nodeToDelete) {
      void setShowConfig(false)
      setSelectedNodeId(null)
    }

    handleSave(updatedNodes, updatedEdges)
  }, [nodeToDelete, setNodes, setEdges, selectedNode, setShowConfig, setSelectedNodeId, handleSave])

  const selectedNodeInfo = useMemo(() => {
    if (!showConfig || !selectedNode) {
      return null
    }

    const nodeId = selectedNode.data?.nodeDefinition ?? selectedNode.type ?? selectedNode.id
    const node = nodeId ? getPipelineNode(nodeId) : null
    const label = selectedNode.data?.label || node?.display.label || nodeId

    return { nodeId, node, label }
  }, [selectedNode, showConfig])

  useEffect(() => {
    const open = showConfig && !!selectedNode && !!selectedNodeInfo
    onConfigModeChange?.(open)

    if (open && selectedNodeInfo) {
      const activePipeline = activePipelineId ? pipelines[activePipelineId] : null
      onConfigContextChange?.({
        pipelineName: activePipeline?.name ?? null,
        pipelineDescription: activePipeline?.description ?? null,
        nodeLabel: selectedNodeInfo.label,
        dirty: isConfigDirty,
        onCancel: handleCloseConfig,
        onSave: handleSaveConfig,
      })
    } else {
      onConfigContextChange?.(null)
    }
  }, [
    activePipelineId,
    handleCloseConfig,
    handleSaveConfig,
    isConfigDirty,
    onConfigContextChange,
    onConfigModeChange,
    pipelines,
    selectedNode,
    selectedNodeInfo,
    showConfig,
  ])

  useEffect(() => () => {
    onConfigModeChange?.(false)
    onConfigContextChange?.(null)
  }, [onConfigContextChange, onConfigModeChange])

  if (showConfig && selectedNode) {
    return (
      <>
        <ConfigurationPanel
          ref={configPanelRef}
          node={selectedNode}
          onClose={() => handleCloseConfig({ force: true })}
          onUpdate={(data) => {
            setNodes(prev => prev.map(node => {
              if (node.id !== selectedNode.id) return node

              // When new data has nested config, remove duplicate top-level keys
              const newData = data as Record<string, unknown>
              const hasNestedConfig = newData.config && typeof newData.config === 'object'

              if (hasNestedConfig) {
                const nestedConfigKeys = Object.keys(newData.config as Record<string, unknown>)
                const oldData = { ...node.data } as Record<string, unknown>
                nestedConfigKeys.forEach(key => {
                  if (key in oldData && key !== 'config') {
                    delete oldData[key]
                  }
                })
                return { ...node, data: { ...oldData, ...data } as PipelineNodeData }
              }

              return { ...node, data: { ...node.data, ...data } }
            }))
          }}
          organizationId={organizationId}
          workspaceId={workspaceId}
          pipelineId={activePipelineId ?? undefined}
          agentId={agentId}
          onDirtyChange={setIsConfigDirty}
        />

        <ConfirmDialog
          open={discardConfirmOpen}
          onOpenChange={setDiscardConfirmOpen}
          title="Discard Changes"
          description="You have unsaved changes. Are you sure you want to close without saving?"
          confirmLabel="Discard"
          cancelLabel="Cancel"
          variant="destructive"
          onConfirm={confirmDiscardChanges}
        />
      </>
    )
  }

  return (
    <>
      <div style={{ width: '100%', height: '100%' }} className="flex flex-col">
        <div
          ref={canvasContainerRef}
          className="relative flex-1 overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
          data-testid="builder-pipeline-canvas"
        >
          <ReactFlow
            style={{ width: '100%', height: '100%' }}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={handleConnect}
            onBeforeDelete={async ({ nodes: nodesToDel }) => {
              if (nodesToDel.length > 0 && !isActiveBinding) {
                handleNodesDelete(nodesToDel as Node<PipelineNodeData>[])
              }
              return false
            }}
            deleteKeyCode={['Backspace', 'Delete']}
            isValidConnection={isValidConnection}
            onNodeClick={selectNode}
            onNodeDoubleClick={handleNodeDoubleClick}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
            proOptions={{ hideAttribution: true }}
            fitView
            fitViewOptions={FIT_VIEW_OPTIONS}
            minZoom={ZOOM.min}
            maxZoom={ZOOM.max}
          >
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            <Controls />
            <MiniMap className="hidden md:block" />

            <Panel position="top-center" className="max-w-[calc(100%-1rem)]">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#e7ecf2] bg-white p-1.5 shadow-[0_2px_8px_rgba(2,8,23,0.08)]">
                <PipelineSelector
                  pipelines={pipelines}
                  activePipelineId={activePipelineId}
                  onSelect={handlePipelineSelect}
                  onClone={handleClonePipeline}
                  onCreate={handleCreatePipeline}
                  onEdit={handleEditPipeline}
                  onDelete={handleRequestDeletePipeline}
                  loading={loadingPipelines}
                  disabled={isSaving}
                  isCreating={isCreating}
                  isSaving={isSaving}
                  isActiveBinding={isActiveBinding}
                  canManage={!agentId}
                />

                <div className="hidden h-6 border-l border-[#eef2f6] sm:block" />

                <Button
                  onClick={handleArrange}
                  size="sm"
                  variant="outline"
                  disabled={nodes.length === 0}
                >
                  <AlignHorizontalJustifyCenter className="h-4 w-4 mr-1" />
                  Arrange
                </Button>

                {activePipelineId && pipelines[activePipelineId] && (
                  <Button
                    onClick={() => handleTogglePipelineHidden(activePipelineId)}
                    size="sm"
                    variant={pipelines[activePipelineId].hidden ? 'default' : 'outline'}
                    disabled={isSaving}
                  >
                    {pipelines[activePipelineId].hidden
                      ? <><EyeOff className="h-4 w-4 mr-1" />Hidden</>
                      : <><Eye className="h-4 w-4 mr-1" />Visible</>
                    }
                  </Button>
                )}
              </div>
            </Panel>
          </ReactFlow>
        </div>
      </div>

      <ConfirmDialog
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        title="Discard Changes"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={confirmDiscardChanges}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Pipeline"
        description={`Are you sure you want to delete "${pipelines[pipelineToDelete ?? '']?.name ?? 'this pipeline'}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={confirmDeletePipeline}
      />

      <ConfirmDialog
        open={nodeDeleteConfirmOpen}
        onOpenChange={setNodeDeleteConfirmOpen}
        title="Delete Node"
        description={`Are you sure you want to delete "${nodes.find(n => n.id === nodeToDelete)?.data?.label ?? 'this node'}"? This will also remove its connections.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={confirmDeleteNode}
      />

      <Dialog open={!!pendingNode} onOpenChange={(open) => { if (!open) cancelAddNode() }}>
        <DialogContent className="sm:max-w-md" data-testid="builder-add-node-dialog">
          <DialogHeader>
            <DialogTitle>Add {pendingNode?.label}</DialogTitle>
            <DialogDescription>
              Enter an instance ID for this node. This identifies the node's settings in the backend.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="instance-id">Instance ID</Label>
            <Input
              id="instance-id"
              data-testid="builder-add-node-instance-id"
              value={pendingInstanceId}
              onChange={(e) => setPendingInstanceId(e.target.value)}
              placeholder={pendingNode?.defaultInstanceId}
              className="mt-1.5"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pendingInstanceId.trim()) {
                  confirmAddNode()
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelAddNode}>Cancel</Button>
            <Button
              data-testid="builder-add-node-confirm"
              onClick={confirmAddNode}
              disabled={!pendingInstanceId.trim()}
            >
              Add Node
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
})

PipelineCanvas.displayName = 'PipelineCanvas'
